import React, { FunctionComponent, useMemo } from 'react';

import Flex from '@gojob/ui-bits/Flex';

import { Global } from '@emotion/core';
import { AutoSizer } from 'react-virtualized/dist/commonjs/AutoSizer';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import { List, ListRowRenderer } from 'react-virtualized/dist/commonjs/List';
import { WindowScroller } from 'react-virtualized/dist/commonjs/WindowScroller';

import InfiniteListItem from 'src/components/InfiniteJobList/InfiniteListItem';
import useVideoPromotion from 'src/components/InfiniteJobList/InfiniteListItem/useVideoPromotion';
import NoResults from 'src/components/NoResults';

import 'react-virtualized/styles.css';

const cellMeasurerCache = new CellMeasurerCache({
  defaultHeight: 176,
  fixedWidth: true,
});

export type InfiniteJobListProps = {
  loading?: boolean;
  query?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  rowCount?: number;
  defaultItemHeight?: number;
  serverWidth?: number;
  serverHeight?: number;
};

export const rowRenderer = (
  loading?: boolean,
  query?: string,
  lat?: number,
  lng?: number,
  radius?: number
): ListRowRenderer => (props) => {
  const { index, key, parent, style } = props;

  return (
    <CellMeasurer columnIndex={0} cache={cellMeasurerCache} key={key} parent={parent} rowIndex={index}>
      {({ registerChild, measure }) => {
        return (
          <InfiniteListItem
            ref={registerChild as any}
            style={style}
            data-testid="LiveJobPreview"
            loading={loading}
            index={index}
            onDisplayChanged={measure}
            query={query}
            lat={lat}
            lng={lng}
            radius={radius}
          />
        );
      }}
    </CellMeasurer>
  );
};

const InfiniteJobList: FunctionComponent<InfiniteJobListProps> = (props) => {
  const { serverHeight, serverWidth, loading, query, lat, lng, radius } = props;

  const hasVideoPromotion = !!useVideoPromotion();
  const rowCount = useMemo(() => {
    const { rowCount = 0 } = props;

    if (loading) {
      return 10;
    }
    if (rowCount === 0) {
      return 0;
    }
    if (hasVideoPromotion) {
      return rowCount + 1;
    }
    return rowCount;
  }, [loading]);

  const renderer = useMemo<ListRowRenderer>(() => {
    return rowRenderer(loading, query, lat, lng, radius);
  }, [loading, query, lat, lng, radius]);

  if (rowCount === 0) {
    return (
      <Flex justifyContent="center" width="100%">
        <NoResults />
      </Flex>
    );
  }
  return (
    <>
      <Global
        /**
         * Sadly we have to override `react-virtualized` internal styles to allow the LiveJobPreview cards to
         * cast shadows outside their parent bounds
         */
        styles={{
          '.ReactVirtualized__Grid.ReactVirtualized__List': {
            overflow: 'visible !important',
            outline: 'none',
          },
          '.ReactVirtualized__Grid__innerScrollContainer': {
            overflow: 'visible !important',
          },
        }}
      />
      <WindowScroller serverHeight={serverHeight} serverWidth={serverWidth}>
        {(windowScrollerProps) => {
          const { height, width, isScrolling, onChildScroll, scrollTop, registerChild } = windowScrollerProps;

          return (
            <AutoSizer disableHeight defaultHeight={height} defaultWidth={width} style={{ width: '100%' }}>
              {({ width }) => (
                <div ref={registerChild} css={{ overscrollBehavior: 'contain' }}>
                  <List
                    autoHeight
                    tabIndex={-1}
                    height={height}
                    width={width}
                    overscanRowCount={5}
                    rowCount={rowCount ? rowCount : 10}
                    estimatedRowSize={cellMeasurerCache.defaultHeight}
                    deferredMeasurementCache={cellMeasurerCache}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                    rowHeight={cellMeasurerCache.rowHeight}
                    rowRenderer={renderer}
                    containerProps={{ 'data-testid': 'InfiniteList' }}
                  />
                </div>
              )}
            </AutoSizer>
          );
        }}
      </WindowScroller>
    </>
  );
};

export default InfiniteJobList;
